import { getIntegrationsDataConfig } from '../../../document-inbox/data/config';
import { getNangoHost } from './config';

type NangoSdk = typeof import('@nangohq/frontend');

let nangoSdk: NangoSdk | undefined;
let nangoSdkLoading: Promise<void> | undefined;

/**
 * Loads the connect SDK before the confirming click so `auth()` stays inside
 * that gesture. Connect stays disabled until this resolves.
 */
export function preloadNangoSdk(): Promise<void> {
  if (nangoSdk) return Promise.resolve();
  nangoSdkLoading ??= import(
    /* webpackPreload: true */
    '@nangohq/frontend'
  ).then(
    (sdk) => {
      nangoSdk = sdk;
    },
    (error: unknown) => {
      nangoSdkLoading = undefined;
      throw error;
    }
  );
  return nangoSdkLoading;
}

export type NangoAuthOutcome = 'connected' | 'closed' | 'blocked' | 'failed';

export interface StartNangoAuthOptions {
  sessionToken: string;
  integration: string;
  /** Failsafe so a sign-in window that never reports back cannot hang the dialog. */
  timeoutMs?: number;
}

export interface StartedNangoAuth {
  result: Promise<NangoAuthOutcome>;
  cancel: () => void;
}

const DEFAULT_CONNECT_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Desktop denies `window.open` unless this gate is armed. Arm it when the
 * dialog opens, not inside the confirming click. An older shell is ignored.
 */
export function setNangoOauthPopupGate(allowed: boolean): void {
  const gate = getIntegrationsDataConfig().oauthPopupGate;
  void gate?.setAllowed(allowed).catch(() => undefined);
}

function outcomeFromAuthError(error: unknown, sdk: NangoSdk): NangoAuthOutcome {
  if (error instanceof sdk.AuthError) {
    if (error.type === 'window_closed') return 'closed';
    if (error.type === 'blocked_by_browser') return 'blocked';
  }
  return 'failed';
}

/**
 * Starts sign-in inside the confirming click, after preload resolves.
 * `detectClosedAuthWindow` settles a closed window. Requires `NEXT_PUBLIC_NANGO_HOST`.
 */
export function startNangoAuth(options: StartNangoAuthOptions): StartedNangoAuth {
  const host = getNangoHost();
  if (!host) {
    return {
      result: Promise.reject(
        new Error('NEXT_PUBLIC_NANGO_HOST is required (do not default to Nango US Cloud)')
      ),
      cancel: () => undefined,
    };
  }

  if (!nangoSdk) {
    return {
      result: Promise.reject(new Error('Connect SDK is not ready')),
      cancel: () => undefined,
    };
  }

  const sdk = nangoSdk;
  const nango = new sdk.default({
    host,
    connectSessionToken: options.sessionToken,
  });
  const timeoutMs = options.timeoutMs ?? DEFAULT_CONNECT_TIMEOUT_MS;

  let settled = false;
  let timeoutId = 0;
  let resolveResult: (outcome: NangoAuthOutcome) => void = () => undefined;
  const result = new Promise<NangoAuthOutcome>((resolve) => {
    resolveResult = resolve;
  });

  const settle = (outcome: NangoAuthOutcome) => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timeoutId);
    if (outcome === 'failed') {
      nango.clear();
      setNangoOauthPopupGate(false);
    }
    resolveResult(outcome);
  };

  timeoutId = window.setTimeout(() => {
    settle('failed');
  }, timeoutMs);

  const cancel = () => {
    nango.clear();
    settle('closed');
  };

  try {
    void nango.auth(options.integration, { detectClosedAuthWindow: true }).then(
      () => settle('connected'),
      (error: unknown) => settle(outcomeFromAuthError(error, sdk))
    );
  } catch (error) {
    settle(outcomeFromAuthError(error, sdk));
  }

  return { result, cancel };
}
